import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { useOrg } from '@/admin/hooks/use-org'

interface ProductSummary {
  uuid: string
  name: string
  status: string
  category: string | null
}

interface ProductAccessData {
  granted_products: ProductSummary[]
  available_products: ProductSummary[]
}

export function usePartnershipProductAccess(partnershipUuid: string | undefined) {
  const { orgUuid } = useOrg()
  return useQuery<ProductAccessData>({
    queryKey: ['partnership-product-access', orgUuid, partnershipUuid],
    queryFn: () =>
      api
        .get(
          `/organisations/${orgUuid}/partnerships/${partnershipUuid}/product-access/`
        )
        .then((r) => r.data.result ?? r.data),
    enabled: !!orgUuid && !!partnershipUuid,
  })
}

interface ProductRosterData {
  available_products: ProductSummary[]
}

/**
 * The upstream org's full product roster, used by the invite dialog to require
 * product selection before any partnership exists (the product-access endpoint
 * needs a partnership UUID that doesn't yet exist at invite time).
 */
export function usePartnershipProductRoster(enabled = true) {
  const { orgUuid } = useOrg()
  return useQuery<ProductRosterData>({
    queryKey: ['partnership-product-roster', orgUuid],
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/partnership-product-roster/`)
        .then((r) => r.data.result ?? r.data),
    enabled: !!orgUuid && enabled,
  })
}

export function useUpdatePartnershipProductAccess(
  partnershipUuid: string | undefined
) {
  const { orgUuid } = useOrg()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (productUuids: string[]) =>
      api
        .put(
          `/organisations/${orgUuid}/partnerships/${partnershipUuid}/product-access/`,
          { product_uuids: productUuids }
        )
        .then((r) => r.data.result ?? r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['partnership-product-access', orgUuid, partnershipUuid],
      })
    },
  })
}
